import { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import CustomSkeleton from '../../Skeleton/CustomSkeleton';
import { Box, Stack, Typography, Slider } from '@mui/material';
import { chartConfig } from '../../../utils/Config';

const formatDollar = (value) => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (absValue >= 1e9) {
    return `${sign}$${(absValue / 1e9)?.toFixed(2)} B`;
  } else if (absValue >= 1e6) {
    return `${sign}$${(absValue / 1e6)?.toFixed(2)} M`;
  } else if (absValue >= 1e3) {
    return `${sign}$${(absValue / 1e3)?.toFixed(2)} K`;
  } else {
    return `${sign}$${absValue?.toFixed(2)}`;
  }
};

const WaterfallChart = ({ title, chartData, id, isWaterfallChartLoading }) => {
  const allValues = chartData?.datasets?.[0]?.data?.flat() || [];
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const [yMin, setYMin] = useState(minValue * 0.9);
  const [yMax, setYMax] = useState(maxValue * 1.05);

  useEffect(() => {
    setYMin(minValue * 0.9);
    setYMax(maxValue * 1.05);
  }, [chartData]);

  if (!chartData || !chartData?.datasets) return null;

  const handleSliderChange = (_, newValue) => {
    setYMin(newValue[0]);
    setYMax(newValue[1]);
  };

  chartData.datasets[0].backgroundColor = chartData?.datasets?.[0]?.data?.map(([prev, curr]) =>
    curr < prev ? 'red' : '#33ABDA'
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false, // Disable aspect ratio to control height independently
    animation: false,
    scales: {
      y: {
        min: yMin,
        max: yMax,
        title: { display: true },
        ticks: {
          callback: (value) => formatDollar(value)
        }
      }
    },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            if (Array.isArray(value)) {
              const diff = value[1] - value[0];
              return `$ ${diff?.toLocaleString()}`;
            }
            return `$ ${value?.toLocaleString()}`;
          }
        }
      }
    },
    interaction: chartConfig,
    barThickness: 50, // Fixed bar width
    categoryPercentage: 16, // Adjusts spacing between bars
    barPercentage: 10 // Makes bars fill more space within the category
  };

  return isWaterfallChartLoading ? (
    <Box
      data-testid="custom-skeleton"
      id={id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 24px',
        borderRadius: '8px',
        border: '1px solid #DBEAFE',
        backgroundColor: '#F8FAFC',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '200px'
      }}>
      <CustomSkeleton numberOfSkeleton={5} />
    </Box>
  ) : (
    <Box
      id={id}
      sx={{
        display: 'flex',
        flexDirection: 'row', // Align chart and slider side by side
        padding: '16px 24px',
        borderRadius: '8px',
        border: '1px solid #DBEAFE',
        backgroundColor: '#F8FAFC',
        height: '400px', // Fixed height for the container
        width: '100%', // Allow width to adjust
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1rem'
      }}>
      {/* Chart */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
        <Box sx={{ width: '100%', height: '100%' }}>
          <Stack justifyContent={'flex-start'} direction={'row'} gap={2}>
            <Typography variant="overline" sx={{ fontWeight: 'bold', color: '#374151' }}>
              {title}
            </Typography>
          </Stack>
          <Box sx={{ marginTop: '16px', width: '100%', height: 'calc(100% - 40px)' }}>
            <Bar data={chartData} options={options} />
          </Box>
        </Box>
      </Box>

      {/* Vertical Slider */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: '1rem', // Add some space between the chart and the slider
          height: '100%'
        }}>
        <Slider
          orientation="vertical"
          value={[yMin, yMax]}
          onChange={handleSliderChange}
          min={minValue * 0.9}
          max={maxValue * 1.05}
          step={1}
          sx={{
            color: '#B05788',
            height: '350px',
            width: '12px', // Set the thickness of the slider
            '& .MuiSlider-thumb': {
              backgroundColor: 'white', // Set the circles to white
              border: '2px solid #B05788' // Optional: Add a border to make the thumb stand out
            },
            '& .MuiSlider-rail': {
              backgroundColor: '#e0e0e0' // Light gray rail color
            },
            '& .MuiSlider-track': {
              backgroundColor: '#B05788' // Set the track between the thumbs to pink
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default WaterfallChart;
